import {
  DocumentColors,
  HaidiSchema,
  LightDocument,
  documentContainer,
  formatDocumentBreadcrumb,
  formatPatientBreadcrumb,
  getDocumentTimestamp,
  useFormatters,
} from '@wallaby-ui/document';
import {RawDocumentData} from '@wallaby-ui/document/types/js/containers/document';
import * as React from 'react';
import {Helmet} from 'react-helmet';
import {useParams} from 'react-router-dom';

import {NotFoundComponentBox} from '../NotFoundComponentBox';

interface OwnProps {
  documentIsLoading: boolean;
  document: RawDocumentData | undefined;
}

type Props = OwnProps;

export const EncounterDocumentLayout: React.FC<Props> = (props) => {
  const {documentIsLoading, document} = props;
  const {documentId, encounterId} = useParams();

  const storageIdPrefix = `/encounter/${encounterId}/document/${documentId}`;
  const haidiDocument = document as HaidiSchema.components['schemas']['DocumentViewDto'];
  const formatters = useFormatters();
  const patientLabel = formatPatientBreadcrumb({
    isLoading: documentIsLoading,
    id: haidiDocument?.patientProperties.patientId,
    name: haidiDocument?.patientProperties.patientFullName,
    birthNumber: haidiDocument?.patientProperties.patientIdentificationNumber,
  });
  const documentLabel = formatDocumentBreadcrumb(
    {
      isLoading: documentIsLoading,
      id: documentId,
      name: haidiDocument?.documentProperties.documentName,
      timestamp: getDocumentTimestamp(haidiDocument),
    },
    formatters
  );
  // Per PDW-48/PDW-49: `documentIsMedicationPlaceholder` is the source of truth
  // that decides whether to render the structured-medication placeholder table
  // or the regular document content. Its location varies across BE versions:
  // the running backend nests it inside `documentProperties`, while the OpenAPI
  // spec (generate:types source) exposes it at the DocumentViewDto root — read
  // both so the table renders regardless of which build is deployed. The field
  // name intentionally differs from the homonymous `isMedicationPlaceholder`
  // used on timeline annotations / docs-list / merge DTOs to avoid collisions.
  const isMedicationPlaceholder = Boolean(
    haidiDocument?.documentIsMedicationPlaceholder ??
      (haidiDocument?.documentProperties as {documentIsMedicationPlaceholder?: boolean} | undefined)
        ?.documentIsMedicationPlaceholder
  );

  return (
    <>
      <Helmet title={[patientLabel, documentLabel].join(' | ')} />
      {documentId ? (
        isMedicationPlaceholder ? (
          <documentContainer.MedicationPlaceholderDocument documentVersionId={documentId} />
        ) : (
          <LightDocument
            storageId={`${storageIdPrefix}.doc`}
            document={document as any}
            heightOffset={-98}
            getDocumentColors={getHaidiDocumentColors}
            snippetPriorityTable={HAIDI_SNIPPET_PRIORITY_TABLE}
          />
        )
      ) : (
        <NotFoundComponentBox />
      )}
    </>
  );
};

EncounterDocumentLayout.displayName = 'EncounterDocumentLayout';

type HaidiDocumentEventTypeEnum = HaidiSchema.components['schemas']['HaidiDocumentEventTypeEnum'];
function getHaidiDocumentColors(eventType: string): DocumentColors {
  type DocumentColorsPartial = Omit<DocumentColors, 'text'> & {text?: string};
  let colorsPartial: DocumentColorsPartial = {background: '#E2E8F2', highlight: '#9BA8BD'};
  const COLOR_MAP: Record<HaidiDocumentEventTypeEnum, DocumentColorsPartial> = {
    BSI: {background: '#FFD6D6', highlight: '#FF5353'},
    CRI: {background: '#FFDAE9', highlight: '#E772A3'},
    GI: {background: '#FFEDD2', highlight: '#FF9900'},
    PN: {background: '#CBC7FF', highlight: '#2115A6'},
    LRI: {background: '#EFDCFF', highlight: '#6213A4'},
    URI: {background: '#E1F7FF', highlight: '#2D99CD'},
    SSI: {background: '#EDFFA5', highlight: '#9CB735'}, // surgery
    UTI: {background: '#CDFBE0', highlight: '#07DE5D'},
    ETIOLOGY: {background: '#E2E8F2', highlight: '#5D6F88'},
    MICROBIOLOGY: {background: '#FFD7F4', highlight: '#FF00B8'},
    MEDICATION: {background: '#D8FFF8', highlight: '#00C4B8'},
    ABDOMINAL_PAIN: {background: '#FFEDD2', highlight: '#FF9900'},
    CATH: {background: '#D8E8FF', highlight: '#6FA9FF'},
    STATE: {background: '#E2E8F2', highlight: '#5D6F88'},
    TEMPERATURE: {background: '#FFDACA', highlight: '#FF8717'},
    CRP_MENTION: {background: '#CBEACA', highlight: '#007A05'},
    LEU_MENTION: {background: '#CBEACA', highlight: '#007A05'},
    RIGORS: {background: '#ECD8C6', highlight: '#8C5A2D'},
    SURGERY_MENTION: {background: '#EDFFA5', highlight: '#9CB735'},
  };
  if (eventType in COLOR_MAP) {
    colorsPartial = COLOR_MAP[eventType];
  }
  return {
    text: 'var(--dlAgGridFontColor)',
    ...colorsPartial,
  };
}
const HAIDI_SNIPPET_PRIORITY_TABLE: HaidiDocumentEventTypeEnum[] = [
  'BSI',
  'CRI',
  'GI',
  'PN',
  'LRI',
  'URI',
  'SSI', // surgery
  'UTI',
  'ETIOLOGY',
  'MICROBIOLOGY',
  'MEDICATION',
  'ABDOMINAL_PAIN',
  'CATH',
  'STATE',
  'TEMPERATURE',
  'CRP_MENTION',
  'LEU_MENTION',
  'RIGORS',
  'SURGERY_MENTION',
];
